(() => {
  'use strict';

  if (window.AmelieGiftBoxOfferV1) return;
  window.AmelieGiftBoxOfferV1 = true;

  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  let refreshTimer = 0;
  let requestInFlight = null;
  let refreshQueued = false;
  let latestState = { giftQuantity: 0, legacyGiftQuantity: 0, qualifyingQuantity: 0, giftLines: [], legacyGiftLines: [] };
  let normalizationInFlight = null;
  let pendingCheckoutButton = null;
  let skipPromptForPage = false;

  const offers = () => Array.from(document.querySelectorAll('[data-amelie-gift-box-offer]'));

  const cartUrl = () => {
    const root = window.Shopify?.routes?.root || window.T4Sroutes?.root_url || '/';
    return `${root.replace(/\/$/, '')}/cart.js`;
  };

  const cartAddUrl = () => {
    const root = window.Shopify?.routes?.root || window.T4Sroutes?.root_url || '/';
    return `${root.replace(/\/$/, '')}/cart/add.js`;
  };

  const cartUpdateUrl = () => {
    const root = window.Shopify?.routes?.root || window.T4Sroutes?.root_url || '/';
    return `${root.replace(/\/$/, '')}/cart/update.js`;
  };

  const readIds = () => {
    const offer = offers()[0];
    return {
      productId: Number(offer?.dataset.productId || 0),
      variantId: Number(offer?.dataset.variantId || 0),
    };
  };

  const buildState = (cart) => {
    const { productId, variantId } = readIds();
    let giftQuantity = 0;
    let legacyGiftQuantity = 0;
    let qualifyingQuantity = 0;
    const giftLines = [];
    const legacyGiftLines = [];

    (cart.items || []).forEach((item) => {
      const isLegacyGift = Number(item.product_id) === 10323362382134 || Number(item.variant_id) === 52943538913590;
      const isGift = Number(item.product_id) === productId || Number(item.variant_id) === variantId;
      if (isLegacyGift) {
        legacyGiftQuantity += Number(item.quantity || 0);
        if (item.key) legacyGiftLines.push({ key: item.key, quantity: Number(item.quantity || 0) });
      }
      else if (isGift) {
        giftQuantity += Number(item.quantity || 0);
        if (item.key) giftLines.push({ key: item.key, quantity: Number(item.quantity || 0) });
      }
      else qualifyingQuantity += Number(item.quantity || 0);
    });

    return { giftQuantity, legacyGiftQuantity, qualifyingQuantity, giftLines, legacyGiftLines };
  };

  const updateOffer = (offer, state) => {
    const isAdded = state.giftQuantity > 0;
    const shouldShow = state.qualifyingQuantity > 0 || isAdded;
    const button = offer.querySelector('[data-amelie-gift-button]');
    const buttonText = offer.querySelector('[data-amelie-gift-button-text]');
    const price = offer.querySelector('[data-amelie-gift-price]');

    offer.hidden = !shouldShow;
    offer.classList.toggle('is-added', isAdded);
    offer.classList.remove('is-loading');
    if (!button || !buttonText || !price) return;

    buttonText.textContent = isAdded ? offer.dataset.addedLabel : offer.dataset.addLabel;
    price.textContent = isAdded ? offer.dataset.appliedLabel : offer.dataset.readyLabel;
    button.setAttribute('aria-disabled', String(isAdded));
    if (isAdded) button.setAttribute('tabindex', '-1');
    else button.removeAttribute('tabindex');
  };

  const updateOffers = (cart) => {
    const state = buildState(cart);
    latestState = state;
    offers().forEach((offer) => updateOffer(offer, state));
  };

  const normalizeGiftQuantity = async (cart) => {
    const state = buildState(cart);
    const desiredQuantity = state.qualifyingQuantity > 0 ? Math.min(state.giftQuantity + state.legacyGiftQuantity, 1) : 0;
    const isNormalized = state.legacyGiftQuantity === 0 && state.giftQuantity === desiredQuantity && state.giftLines.length <= 1;
    const { variantId } = readIds();
    if (!nativeFetch || !variantId || isNormalized || state.giftLines.length + state.legacyGiftLines.length === 0) {
      updateOffers(cart);
      return cart;
    }
    if (normalizationInFlight) return normalizationInFlight;

    const updates = {};
    state.legacyGiftLines.forEach((line) => { updates[line.key] = 0; });
    state.giftLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? desiredQuantity : 0;
    });
    if (desiredQuantity > 0 && state.giftLines.length === 0) updates[variantId] = desiredQuantity;

    normalizationInFlight = nativeFetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update ${response.status}`);
        return response.json();
      })
      .then((updatedCart) => {
        updateOffers(updatedCart);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return updatedCart;
      })
      .finally(() => {
        normalizationInFlight = null;
      });

    return normalizationInFlight;
  };

  const refresh = async () => {
    window.clearTimeout(refreshTimer);
    if (!nativeFetch || offers().length === 0) return;
    if (requestInFlight) {
      refreshQueued = true;
      return requestInFlight;
    }

    refreshQueued = false;
    requestInFlight = nativeFetch(cartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart ${response.status}`);
        return response.json();
      })
      .then(normalizeGiftQuantity)
      .catch(() => {
        offers().forEach((offer) => offer.classList.remove('is-loading'));
      })
      .finally(() => {
        requestInFlight = null;
        if (refreshQueued) scheduleRefresh(50);
      });

    return requestInFlight;
  };

  const scheduleRefresh = (delay = 120) => {
    window.clearTimeout(refreshTimer);
    if (requestInFlight) {
      refreshQueued = true;
      return;
    }
    refreshTimer = window.setTimeout(refresh, delay);
  };

  const promptSource = () => offers().find((offer) => !offer.hidden) || offers()[0];

  const ensurePrompt = () => {
    let dialog = document.querySelector('[data-amelie-gift-prompt]');
    if (dialog) return dialog;

    const source = promptSource();
    if (!source) return null;

    dialog = document.createElement('dialog');
    dialog.className = 'amelie-gift-confirm';
    dialog.dataset.amelieGiftPrompt = '';
    dialog.setAttribute('aria-labelledby', 'AmelieGiftConfirmTitle');
    dialog.innerHTML = `
      <div class="amelie-gift-confirm__card">
        <button class="amelie-gift-confirm__close" type="button" data-amelie-gift-prompt-close>
          <span aria-hidden="true">×</span>
        </button>
        <div class="amelie-gift-confirm__visual">
          <img class="amelie-gift-confirm__image" alt="" width="132" height="132">
          <span aria-hidden="true">🎄</span>
        </div>
        <div class="amelie-gift-confirm__eyebrow" data-amelie-gift-prompt-eyebrow></div>
        <h2 id="AmelieGiftConfirmTitle" data-amelie-gift-prompt-title></h2>
        <p data-amelie-gift-prompt-body></p>
        <div class="amelie-gift-confirm__price"><s data-amelie-gift-original-price></s><strong data-amelie-gift-free></strong></div>
        <p class="amelie-gift-confirm__error" data-amelie-gift-prompt-error hidden></p>
        <div class="amelie-gift-confirm__actions">
          <button type="button" class="amelie-gift-confirm__add" data-amelie-gift-prompt-add></button>
          <button type="button" class="amelie-gift-confirm__skip" data-amelie-gift-prompt-skip></button>
        </div>
      </div>
    `;

    dialog.querySelector('[data-amelie-gift-prompt-eyebrow]').textContent = source.querySelector('.amelie-gift-box__eyebrow')?.textContent || '';
    dialog.querySelector('[data-amelie-gift-prompt-title]').textContent = source.dataset.promptTitle;
    dialog.querySelector('[data-amelie-gift-prompt-body]').textContent = source.dataset.promptBody;
    dialog.querySelector('[data-amelie-gift-original-price]').textContent = source.dataset.originalPrice;
    dialog.querySelector('[data-amelie-gift-free]').textContent = source.dataset.freeLabel;
    dialog.querySelector('[data-amelie-gift-prompt-add]').textContent = source.dataset.promptAdd;
    dialog.querySelector('[data-amelie-gift-prompt-skip]').textContent = source.dataset.promptSkip;
    dialog.querySelector('[data-amelie-gift-prompt-error]').textContent = source.dataset.promptError;
    dialog.querySelector('[data-amelie-gift-prompt-close]').setAttribute('aria-label', source.dataset.promptClose);
    dialog.querySelector('.amelie-gift-confirm__image').src = source.dataset.imageUrl;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-amelie-gift-prompt-close]')) {
        dialog.close();
      }
    });

    dialog.querySelector('[data-amelie-gift-prompt-skip]').addEventListener('click', () => {
      skipPromptForPage = true;
      dialog.close();
      const checkoutButton = pendingCheckoutButton;
      pendingCheckoutButton = null;
      window.setTimeout(() => checkoutButton?.click(), 0);
    });

    dialog.querySelector('[data-amelie-gift-prompt-add]').addEventListener('click', async () => {
      const addButton = dialog.querySelector('[data-amelie-gift-prompt-add]');
      const error = dialog.querySelector('[data-amelie-gift-prompt-error]');
      const sourceOffer = promptSource();
      const variantId = Number(sourceOffer?.dataset.variantId || 0);

      if (!nativeFetch || !variantId) return;
      addButton.disabled = true;
      addButton.textContent = sourceOffer.dataset.loadingLabel;
      error.hidden = true;

      try {
        const response = await nativeFetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(`Cart add ${response.status}`);

        const cartResponse = await nativeFetch(cartUrl(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error(`Cart ${cartResponse.status}`);
        updateOffers(await cartResponse.json());

        dialog.close();
        const checkoutButton = pendingCheckoutButton;
        pendingCheckoutButton = null;
        window.setTimeout(() => checkoutButton?.click(), 40);
      } catch (_error) {
        error.hidden = false;
        addButton.disabled = false;
        addButton.textContent = sourceOffer.dataset.promptAdd;
      }
    });

    document.body.append(dialog);
    return dialog;
  };

  const shouldPromptForGift = () => {
    if (skipPromptForPage || latestState.giftQuantity > 0) return false;
    return offers().some((offer) => !offer.hidden && !offer.classList.contains('is-added'));
  };

  const guardCheckout = (event, checkoutButton) => {
    if (!checkoutButton) return;
    const hasInvalidGiftQuantity =
      latestState.legacyGiftQuantity > 0 ||
      latestState.giftQuantity > 1 ||
      (latestState.giftQuantity > 0 && latestState.qualifyingQuantity === 0);
    if (hasInvalidGiftQuantity) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      pendingCheckoutButton = checkoutButton;
      refresh().then((cart) => {
        const button = pendingCheckoutButton;
        pendingCheckoutButton = null;
        if (cart && latestState.legacyGiftQuantity === 0 && latestState.giftQuantity <= 1) {
          window.setTimeout(() => button?.click(), 40);
        }
      });
      return;
    }
    if (!shouldPromptForGift()) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    pendingCheckoutButton = checkoutButton;
    const dialog = ensurePrompt();
    if (dialog && !dialog.open) dialog.showModal();
  };

  document.addEventListener(
    'click',
    (event) => {
      guardCheckout(
        event,
        event.target.closest?.('button[name="checkout"], input[name="checkout"]')
      );
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (event.submitter?.matches?.('button[name="checkout"], input[name="checkout"]')) {
        guardCheckout(event, event.submitter);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('[data-amelie-gift-button]');
      if (!button) return;
      const offer = button.closest('[data-amelie-gift-box-offer]');

      if (button.getAttribute('aria-disabled') === 'true' || offer?.classList.contains('is-loading')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      offer?.classList.add('is-loading');
      const text = offer?.querySelector('[data-amelie-gift-button-text]');
      if (text) text.textContent = offer.dataset.loadingLabel;
      window.setTimeout(() => scheduleRefresh(0), 450);
      window.setTimeout(() => scheduleRefresh(0), 1100);
    },
    true
  );

  ['cart:refresh', 'cart:updated', 'cart:open'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleRefresh(30));
  });

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[data-amelie-gift-box-offer], [data-cart-item]') ||
            node.querySelector?.('[data-amelie-gift-box-offer], [data-cart-item]'))
      )
    );
    if (cartChanged) scheduleRefresh(50);
  });

  const init = () => {
    if (!document.body) {
      window.setTimeout(init, 0);
      return;
    }
    const initialOffer = offers()[0];
    if (initialOffer) {
      latestState.giftQuantity = Number(initialOffer.dataset.giftQuantity || 0);
      latestState.legacyGiftQuantity = Number(initialOffer.dataset.legacyGiftQuantity || 0);
      latestState.qualifyingQuantity = Number(initialOffer.dataset.qualifyingQuantity || 0);
    }
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
